// The room screen's invite-link affordance: the URL to share, and copying it to the clipboard. Split out
// of lobbyView.ts since it needs no room/lobby state, only a document/navigator to act on — and so its
// fallback path is unit-testable without lobbyView.ts's own DOM wiring.
//
// `navigator.clipboard.writeText` is the normal path, but the deployed site itself serves plain http from
// a bare IP (no certificate for an IP address is realistic), where `navigator.clipboard` is undefined
// outright — the Clipboard API is restricted to secure contexts, and that is not a corner case here, it is
// the production deployment. `document.execCommand('copy')` on a hidden, selected textarea is the fallback
// that still works in an insecure context; a caller whose environment can't manage even that (both paths
// unavailable or denied) is expected to fall back further, to showing the link in a selectable field —
// this module only reports success or failure, the field itself is lobbyView.ts's concern.
import type { RoomId } from '../../shared/protocol.js';

export function roomInviteUrl(roomId: RoomId): string {
  const url = new URL(location.href);
  url.search = `?room=${encodeURIComponent(roomId)}`;
  url.hash = '';
  return url.toString();
}

// `writeText()`'s promise is observed to simply never settle, neither resolving nor rejecting, when
// Chrome parks a permission decision it has nobody to ask (no real user-activation behind the call, e.g.
// automated/embedded contexts) — with no timeout, that leaves the click with no feedback and no fallback
// forever, indistinguishable from a frozen page. A user's activation is also transient: by the time a
// slow permission prompt would resolve, it may already have expired, so racing a bound and falling back
// is correct even outside that stuck-forever case, not just a safety net for it.
const CLIPBOARD_TIMEOUT_MS = 1500;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('clipboard timed out')), ms);
    promise.then(
      v => { clearTimeout(timer); resolve(v); },
      e => { clearTimeout(timer); reject(e); },
    );
  });
}

/** Resolves true once `text` is actually on the system clipboard. */
export async function copyToClipboard(text: string): Promise<boolean> {
  const clipboard = navigator.clipboard;
  if (clipboard?.writeText) {
    try {
      await withTimeout(clipboard.writeText(text), CLIPBOARD_TIMEOUT_MS);
      return true;
    } catch {
      // Permission denied, disabled in this embed, or simply timed out — fall through to execCommand.
    }
  }
  return copyWithExecCommand(text);
}

function copyWithExecCommand(text: string): boolean {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  // Off-screen, not `display:none` — an element hidden that way is unfocusable/unselectable in some
  // browsers, which would make select() below a no-op and execCommand('copy') copy nothing.
  ta.style.position = 'fixed';
  ta.style.top = '0';
  ta.style.left = '0';
  ta.style.opacity = '0';
  ta.style.pointerEvents = 'none';
  document.body.appendChild(ta);
  let ok = false;
  try {
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    ok = document.execCommand('copy');
  } catch {
    // A host missing select()/setSelectionRange()/execCommand entirely (some minimal embeds) fails the
    // same way a denied permission does — the caller falls back further, to a selectable field.
    ok = false;
  } finally {
    document.body.removeChild(ta);
  }
  return ok;
}
